import React, { useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Button } from './ui/button';
import { Download, QrCode } from 'lucide-react';
import { Card } from './ui/card';

const QRCodeDownload = () => {
  const qrRef = useRef(null);
  const productionUrl = 'https://minchsolutions.site/';

  const downloadQRCode = () => {
    const canvas = qrRef.current.querySelector('canvas');
    if (!canvas) return;

    // Create a new canvas with padding and text
    const finalCanvas = document.createElement('canvas');
    const ctx = finalCanvas.getContext('2d');
    
    // Set dimensions for final image (QR code + padding + text)
    const qrSize = canvas.width;
    const padding = 60;
    const textAreaHeight = 120;
    finalCanvas.width = qrSize + (padding * 2);
    finalCanvas.height = qrSize + (padding * 2) + textAreaHeight;

    // Fill background with white
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);

    // Draw QR code in center
    ctx.drawImage(canvas, padding, padding);

    // Add decorative border (gold gradient effect)
    ctx.strokeStyle = '#D4AF37';
    ctx.lineWidth = 3;
    ctx.strokeRect(padding - 10, padding - 10, qrSize + 20, qrSize + 20);

    // Add text below QR code
    ctx.textAlign = 'center';
    ctx.fillStyle = '#1a4d2e'; // Forest green
    
    // Names
    ctx.font = 'bold 32px Georgia, serif';
    ctx.fillText('Robyn & Angelo', finalCanvas.width / 2, qrSize + padding + 50);
    
    // Subtitle
    ctx.font = '20px Georgia, serif';
    ctx.fillStyle = '#2d5a3d';
    ctx.fillText('Scan to visit our wedding website', finalCanvas.width / 2, qrSize + padding + 85);
    
    // Date
    ctx.font = '16px Georgia, serif';
    ctx.fillStyle = '#666666';
    ctx.fillText('September 18-21, 2026', finalCanvas.width / 2, qrSize + padding + 110);

    // Convert to blob and download
    finalCanvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = 'robyn-angelo-wedding-qr-code.png';
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    });
  };

  return (
    <section className="py-16 bg-background" data-testid="qr-code-section">
      <div className="section-container">
        <div className="text-center mb-8">
          <h2 className="text-4xl md:text-5xl font-serif mb-4 gradient-gold">
            Share Our Website
          </h2>
          <p className="text-lg text-foreground/80 max-w-2xl mx-auto">
            Download the QR code to include on invitations, signage, or share with guests
          </p>
        </div>

        <Card className="max-w-md mx-auto p-8 bg-card/50 backdrop-blur border-2 border-primary/20">
          <div className="space-y-6">
            {/* QR Code Preview */}
            <div 
              ref={qrRef}
              className="flex justify-center p-6 bg-white rounded-lg"
              data-testid="qr-code-preview"
            >
              <QRCodeCanvas
                value={productionUrl}
                size={220}
                level="H"
                includeMargin={true}
                fgColor="#1a4d2e"
                bgColor="#ffffff"
              />
            </div>

            {/* Info */}
            <div className="text-center space-y-2">
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <QrCode className="w-4 h-4 text-primary" />
                <span>Links to: {productionUrl}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                High quality QR code perfect for printing
              </p>
            </div>

            {/* Download Button */}
            <Button
              onClick={downloadQRCode}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
              size="lg"
              data-testid="download-qr-button"
            >
              <Download className="w-5 h-5 mr-2" />
              Download QR Code
            </Button>
          </div>
        </Card>

        <div className="mt-8 text-center text-sm text-muted-foreground max-w-xl mx-auto">
          <p>
            The downloaded image includes your names, the QR code, and wedding dates - 
            ready to use on save-the-dates, invitations, or wedding signage
          </p>
        </div>
      </div>
    </section>
  );
};

export default QRCodeDownload;
